import {useEffect, useState} from "react";
import {Button, Card, Col, message, Row, Statistic, Table} from "antd";
import {PrinterOutlined, ReloadOutlined} from "@ant-design/icons";
import Api from "../../services/Api";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import flexCurrency from "../../utils/FlexCurrency";

dayjs.locale("tr");
dayjs.extend(isSameOrAfter);

const printStyle = `
  .fk-haftalik-table .ant-table-cell { padding: 3px !important; }
  @media print {
    body * { visibility: hidden; }
    #fk-haftalik-print-area, #fk-haftalik-print-area * { visibility: visible; }
    #fk-haftalik-print-area { position: absolute; top: 0; left: 0; width: 100%; }
    .no-print { display: none !important; }
  }
`;

const GISE_HEX = {1: "#1677ff", 2: "#722ed1"};

const columns = [
    {
        title: "Tarih",
        dataIndex: "date",
        render: d => dayjs(d).format("DD MMMM YYYY dddd"),
    },
    {
        title: "Çekim",
        dataIndex: "shots",
        className: "text-align-right",
        render: v => v.toLocaleString("tr-TR"),
    },
    {
        title: "Satış",
        dataIndex: "sales",
        className: "text-align-right",
        render: v => v.toLocaleString("tr-TR"),
    },
    {
        title: <span style={{color: GISE_HEX[1]}}>1. Gişe Tutar (₺)</span>,
        dataIndex: "amount1",
        className: "text-align-right",
        render: v => <b style={{color: GISE_HEX[1]}}>{flexCurrency(v)}</b>,
    },
    {
        title: <span style={{color: GISE_HEX[2]}}>2. Gişe Tutar (₺)</span>,
        dataIndex: "amount2",
        className: "text-align-right",
        render: v => <b style={{color: GISE_HEX[2]}}>{flexCurrency(v)}</b>,
    },
    {
        title: "Toplam Tutar (₺)",
        className: "text-align-right",
        render: r => <b style={{color: "#52c41a"}}>{flexCurrency(r.amount1 + r.amount2)}</b>,
    },
];

const groupByMonth = (items) => {
    const groups = {};
    items.forEach(item => {
        const ay = item.date.slice(0, 7);
        if (!groups[ay]) groups[ay] = [];
        groups[ay].push(item);
    });

    return Object.keys(groups)
        .sort((a, b) => b.localeCompare(a))
        .map(ay => {
            const gunler = [...groups[ay]].sort((a, b) => new Date(b.date) - new Date(a.date));
            const total = {
                shots:   gunler.reduce((s, r) => s + r.shots,   0),
                sales:   gunler.reduce((s, r) => s + r.sales,   0),
                amount1: gunler.reduce((s, r) => s + r.amount1, 0),
                amount2: gunler.reduce((s, r) => s + r.amount2, 0),
            };
            const label = dayjs(`${ay}-01`).format("MMMM YYYY");
            return {ay, label, gunler, total};
        });
};

/* ── Ay özet şeridi ─────────────────────────────────────── */
const OzetSerit = ({total}) => (
    <Row
        gutter={[16, 8]}
        style={{padding: "10px 16px", background: "#fafafa", borderBottom: "1px solid #f0f0f0"}}
    >
        <Col xs={12} sm={4}>
            <Statistic
                title="Çekim"
                value={total.shots}
                valueStyle={{fontSize: 13}}
                formatter={v => v.toLocaleString("tr-TR")}
            />
        </Col>
        <Col xs={12} sm={4}>
            <Statistic
                title="Satış"
                value={total.sales}
                valueStyle={{fontSize: 13}}
                formatter={v => v.toLocaleString("tr-TR")}
            />
        </Col>
        <Col xs={12} sm={5}>
            <Statistic
                title="1. Gişe"
                value={total.amount1}
                suffix="₺"
                valueStyle={{fontSize: 13, color: GISE_HEX[1]}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
        <Col xs={12} sm={5}>
            <Statistic
                title="2. Gişe"
                value={total.amount2}
                suffix="₺"
                valueStyle={{fontSize: 13, color: GISE_HEX[2]}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
        <Col xs={24} sm={6}>
            <Statistic
                title="Toplam Tutar"
                value={total.amount1 + total.amount2}
                suffix="₺"
                valueStyle={{fontSize: 14, color: "#52c41a", fontWeight: 700}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
    </Row>
);

/* ── Haftalık özet kartı ────────────────────────────────── */
const HaftalikOzet = ({toplam}) => (
    <Card
        style={{
            marginBottom: 12,
            background: "linear-gradient(135deg, #e6f4ff 0%, #f0f5ff 100%)",
            border: "1px solid #91caff",
        }}
        bodyStyle={{padding: "12px 16px"}}
    >
        <Row align="middle" gutter={[24, 8]}>
            <Col xs={24} sm={4}>
                <div style={{fontSize: 16, fontWeight: 800, color: "#1677ff"}}>Son 7 Gün</div>
                <div style={{fontSize: 11, color: "#888", marginTop: 2}}>Haftalık Özet</div>
            </Col>
            <Col xs={12} sm={4}>
                <Statistic
                    title="Toplam Çekim"
                    value={toplam.shots}
                    valueStyle={{fontSize: 14, fontWeight: 600}}
                    formatter={v => v.toLocaleString("tr-TR")}
                />
            </Col>
            <Col xs={12} sm={4}>
                <Statistic
                    title="Toplam Satış"
                    value={toplam.sales}
                    valueStyle={{fontSize: 14, fontWeight: 600}}
                    formatter={v => v.toLocaleString("tr-TR")}
                />
            </Col>
            <Col xs={12} sm={4}>
                <Statistic
                    title="1. Gişe Tutar"
                    value={toplam.amount1}
                    suffix="₺"
                    valueStyle={{fontSize: 14, color: GISE_HEX[1], fontWeight: 600}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
            <Col xs={12} sm={4}>
                <Statistic
                    title="2. Gişe Tutar"
                    value={toplam.amount2}
                    suffix="₺"
                    valueStyle={{fontSize: 14, color: GISE_HEX[2], fontWeight: 600}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
            <Col xs={24} sm={4}>
                <Statistic
                    title="Genel Toplam"
                    value={toplam.amount1 + toplam.amount2}
                    suffix="₺"
                    valueStyle={{fontSize: 16, color: "#52c41a", fontWeight: 800}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
        </Row>
    </Card>
);

/* ── Ana bileşen ────────────────────────────────────────── */
const FotoKapanHaftalikRaporPage = () => {
    const [monthData, setMonthData] = useState([]);
    const [haftalikToplam, setHaftalikToplam] = useState(null);
    const [loading, setLoading] = useState(false);

    const getData = () => {
        setLoading(true);
        Api.get("/api/FotoKapan/GetTotalKazanclar")
            .then(res => {
                const baslangic = dayjs().subtract(6, "day").startOf("day");
                const filtrelenmis = res.data.filter(item =>
                    dayjs(item.date).isSameOrAfter(baslangic)
                );

                // Tarihe göre tek satır (1. ve 2. gişeyi birleştir)
                const uniqueDates = [...new Set(filtrelenmis.map(i => i.date))]
                    .sort((a, b) => new Date(b) - new Date(a));

                const tableRows = uniqueDates.map(date => {
                    const s1 = filtrelenmis.find(i => i.date === date && i.station_id === 1);
                    const s2 = filtrelenmis.find(i => i.date === date && i.station_id === 2);
                    return {
                        date,
                        shots:   (s1 ?? s2)?.shots ?? 0,
                        sales:   (s1 ?? s2)?.sales ?? 0,
                        amount1: s1?.amount ?? 0,
                        amount2: s2?.amount ?? 0,
                    };
                });

                const gruplu = groupByMonth(tableRows);
                setMonthData(gruplu);

                setHaftalikToplam({
                    shots:   tableRows.reduce((s, r) => s + r.shots,   0),
                    sales:   tableRows.reduce((s, r) => s + r.sales,   0),
                    amount1: tableRows.reduce((s, r) => s + r.amount1, 0),
                    amount2: tableRows.reduce((s, r) => s + r.amount2, 0),
                });
            })
            .catch(() => message.error("Veriler alınırken hata oluştu."))
            .finally(() => setLoading(false));
    };

    useEffect(() => { getData(); }, []);

    return (
        <div className={"p-1"}>
            <style>{printStyle}</style>

            {/* Üst bar */}
            <Card
                className={"no-print"}
                style={{marginBottom: 12}}
                bodyStyle={{padding: "10px 16px"}}
            >
                <Row justify="space-between" align="middle">
                    <Col>
                        <span style={{fontWeight: 700, fontSize: 15}}>Foto Kapan Haftalık Raporu</span>
                    </Col>
                    <Col>
                        <Button
                            icon={<ReloadOutlined/>}
                            onClick={getData}
                            loading={loading}
                            style={{marginRight: 8}}
                        >
                            Yenile
                        </Button>
                        {monthData.length > 0 && (
                            <Button icon={<PrinterOutlined/>} onClick={() => window.print()}>
                                Yazdır
                            </Button>
                        )}
                    </Col>
                </Row>
            </Card>

            {loading && <Card loading style={{marginBottom: 12}}/>}

            <div id={"fk-haftalik-print-area"}>
                {haftalikToplam && <HaftalikOzet toplam={haftalikToplam}/>}

                {monthData.map(({ay, label, gunler, total}) => (
                    <Card
                        key={ay}
                        size={"small"}
                        title={<span style={{fontWeight: 600}}>{label}</span>}
                        bodyStyle={{padding: 0}}
                        style={{marginBottom: 8}}
                    >
                        <OzetSerit total={total}/>
                        <Table
                            className={"fk-haftalik-table"}
                            size={"small"}
                            bordered
                            dataSource={gunler}
                            rowKey={r => r.date}
                            columns={columns}
                            pagination={false}
                        />
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default FotoKapanHaftalikRaporPage;
